import { z } from "zod";
import { ColorSchema } from "./primitives";

export const TriggerSchema = z.enum(["onClick", "withPrevious", "afterPrevious"]);
export type Trigger = z.infer<typeof TriggerSchema>;

export const Direction4Schema = z.enum(["left", "right", "top", "bottom"]);
export const Direction8Schema = z.enum([
  "left", "right", "top", "bottom", "topLeft", "topRight", "bottomLeft", "bottomRight",
]);
export type Direction4 = z.infer<typeof Direction4Schema>;
export type Direction8 = z.infer<typeof Direction8Schema>;

const Phase = z.enum(["in", "out"]);

export const MotionPathCmdSchema = z.discriminatedUnion("c", [
  z.object({ c: z.literal("M"), x: z.number(), y: z.number() }),
  z.object({ c: z.literal("L"), x: z.number(), y: z.number() }),
  z.object({
    c: z.literal("C"),
    x1: z.number(), y1: z.number(), x2: z.number(), y2: z.number(), x: z.number(), y: z.number(),
  }),
  z.object({ c: z.literal("Z") }),
]);
export type MotionPathCmd = z.infer<typeof MotionPathCmdSchema>;

export const EffectSchema = z.discriminatedUnion("kind", [
  // entrance / exit
  z.object({ kind: z.literal("appear"), phase: Phase }),
  z.object({ kind: z.literal("fade"), phase: Phase }),
  z.object({ kind: z.literal("fly"), phase: Phase, direction: Direction8Schema }),
  z.object({ kind: z.literal("float"), phase: Phase, direction: z.enum(["up", "down"]) }),
  z.object({ kind: z.literal("wipe"), phase: Phase, direction: Direction4Schema }),
  z.object({
    kind: z.literal("split"), phase: Phase,
    orientation: z.enum(["horizontal", "vertical"]), mode: z.enum(["in", "out"]),
  }),
  z.object({ kind: z.literal("zoom"), phase: Phase, origin: z.enum(["object", "slide"]), fromScale: z.number() }),
  z.object({ kind: z.literal("stretch"), phase: Phase, direction: z.union([Direction4Schema, z.literal("across")]) }),
  z.object({ kind: z.literal("peek"), phase: Phase, direction: Direction4Schema }),
  z.object({ kind: z.literal("bounce"), phase: Phase }),
  z.object({ kind: z.literal("wheel"), phase: Phase, spokes: z.number() }),
  // emphasis
  z.object({ kind: z.literal("growShrink"), scaleX: z.number(), scaleY: z.number() }),
  z.object({ kind: z.literal("spin"), degrees: z.number() }),
  z.object({ kind: z.literal("pulse") }),
  z.object({ kind: z.literal("teeter") }),
  z.object({ kind: z.literal("transparency"), opacity: z.number() }),
  z.object({
    kind: z.literal("colorChange"),
    property: z.enum(["fill", "line", "text"]),
    to: ColorSchema,
    persist: z.boolean(),
  }),
  z.object({ kind: z.literal("boldFlash") }),
  z.object({ kind: z.literal("underline") }),
  // motion
  z.object({
    kind: z.literal("motionPath"),
    path: z.array(MotionPathCmdSchema),
    relative: z.boolean().default(true),
    rotateAlongPath: z.boolean().default(false),
  }),
  // media
  z.object({
    kind: z.literal("media"),
    action: z.enum(["play", "playFrom", "pause", "resume", "stop", "togglePause"]),
    at: z.number().optional(),
    bookmark: z.string().optional(),
  }),
  // catch-all
  z.object({ kind: z.literal("fallback"), phase: z.enum(["in", "out", "emph"]), original: z.string() }),
]);
export type Effect = z.infer<typeof EffectSchema>;

export const AnimationTargetSchema = z.object({
  elementId: z.string(),
  scope: z.enum(["shape", "text", "background"]).default("shape"),
  /** inclusive, 0-based paragraph range (p:pRg) */
  paragraphs: z.object({ from: z.number().int(), to: z.number().int() }).optional(),
});
export type AnimationTarget = z.infer<typeof AnimationTargetSchema>;

export const AnimationSchema = z.object({
  id: z.string(),
  trigger: TriggerSchema,
  target: AnimationTargetSchema,
  effect: EffectSchema,
  /** ms */
  duration: z.number().min(0),
  /** ms, relative to the trigger moment */
  delay: z.number().min(0).default(0),
  repeat: z.union([z.number(), z.enum(["untilNextClick", "untilEndOfSlide"])]).default(1),
  autoReverse: z.boolean().default(false),
  rewindAfter: z.boolean().default(false),
  easing: z.object({ accel: z.number().default(0), decel: z.number().default(0) }).default({}),
  source: z
    .object({
      presetClass: z.string(),
      presetID: z.number(),
      presetSubtype: z.number(),
      nodeType: z.string().optional(),
    })
    .optional(),
});
export type Animation = z.infer<typeof AnimationSchema>;

export const InteractiveSequenceSchema = z.object({
  triggerElementId: z.string(),
  animations: z.array(AnimationSchema),
});
export type InteractiveSequence = z.infer<typeof InteractiveSequenceSchema>;

export const TimelineSchema = z.object({
  main: z.array(AnimationSchema).default([]),
  interactive: z.array(InteractiveSequenceSchema).default([]),
});
export type Timeline = z.infer<typeof TimelineSchema>;

export const TransitionSchema = z.object({
  kind: z.enum([
    "none", "fade", "fadeThroughBlack", "push", "wipe", "cut", "cover", "uncover", "split", "zoom", "fallback",
  ]),
  direction: Direction8Schema.optional(),
  /** ms */
  duration: z.number().min(0).default(500),
  advanceOnClick: z.boolean().default(true),
  /** ms, null when the slide does not auto-advance */
  advanceAfter: z.number().nullable().default(null),
  original: z.string().optional(),
});
export type Transition = z.infer<typeof TransitionSchema>;
